import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { createSession, logoutSession } from '@/lib/sessionManager';
import { toast } from 'sonner';

/**
 * Hook to manage device sessions on auth state changes.
 * Creates sessions on login, cleans up on logout.
 * 
 * Uses multiple deduplication strategies:
 * 1. User ID tracking - prevents duplicate for same user
 * 2. Lock flag - prevents concurrent creation attempts
 * 3. Timestamp tracking - prevents creation within 10 seconds of last
 */
export function useAuthSessionManager() {
  const navigate = useNavigate();
  const sessionCreatedRef = useRef<string | null>(null);
  const isCreatingSessionRef = useRef(false);
  const lastCreationTimeRef = useRef<number>(0);

  useEffect(() => {
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[useAuthSessionManager] Auth event:', event);

        // Only handle actual SIGNED_IN events, not INITIAL_SESSION
        if (event === 'SIGNED_IN' && session?.user) {
          // Check multiple deduplication conditions
          const now = Date.now();
          const timeSinceLastCreation = now - lastCreationTimeRef.current;
          
          // Skip if:
          // 1. Already created for this user, OR
          // 2. Currently creating, OR
          // 3. Created less than 10 seconds ago
          if (sessionCreatedRef.current === session.user.id || 
              isCreatingSessionRef.current ||
              timeSinceLastCreation < 10000) {
            console.log('[useAuthSessionManager] Skipping duplicate session creation', {
              alreadyCreated: sessionCreatedRef.current === session.user.id,
              isCreating: isCreatingSessionRef.current,
              timeSince: timeSinceLastCreation
            });
            return;
          }

          // Set locks BEFORE the setTimeout to prevent race conditions
          isCreatingSessionRef.current = true;
          lastCreationTimeRef.current = now;

          // Defer session creation to avoid Supabase deadlock
          setTimeout(async () => {
            try {
              const { success, sessionsDeactivated } = await createSession(session.user.id);
              
              if (success) {
                sessionCreatedRef.current = session.user.id;
                
                if (sessionsDeactivated > 0) {
                  toast.info('Other Devices Logged Out', {
                    description: `${sessionsDeactivated} other device(s) were logged out because you reached the maximum device limit.`,
                    duration: 5000
                  });
                }
              }
            } catch (error) {
              console.error('[useAuthSessionManager] Error creating session:', error);
            } finally {
              isCreatingSessionRef.current = false;
            }
          }, 0);
        }

        // Ignore INITIAL_SESSION - it's not a real login, just page load
        if (event === 'INITIAL_SESSION') {
          console.log('[useAuthSessionManager] Ignoring INITIAL_SESSION event');
          return;
        }

        if (event === 'SIGNED_OUT') {
          // Clean up session on logout
          sessionCreatedRef.current = null;
          isCreatingSessionRef.current = false;
          lastCreationTimeRef.current = 0;
          
          // Defer logout to avoid Supabase deadlock
          setTimeout(async () => {
            await logoutSession();
          }, 0);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);
}
